/**
 * A person's form, identity-check and screening status, as the file page draws it — shared by the
 * "People on this file" table and card 2404's "View result" dialog, so the two say one thing in one
 * look. Every word is the engine's; this module only picks the tone and lays the words out.
 */
import { Pill, type PillTone } from "@ardept/design-system";
import type { JobStatusPersonStatus, JobStatusScreeningHit } from "../api/types.js";

/** The pill tone for a person-level status. `not_required` is said in plain text instead. */
const PERSON_STATUS_TONE: Readonly<Record<JobStatusPersonStatus["state"], PillTone>> = {
  not_started: "neutral",
  sent: "waiting",
  in_progress: "progress",
  done: "good",
  failed: "bad",
  not_required: "muted",
};

/** One person-level status: a pill in the engine's words, or plain text when it is not needed. */
export function PersonStatus({ status }: { status: JobStatusPersonStatus }): JSX.Element {
  if (status.state === "not_required") return <span className="fd-muted">{status.label}</span>;
  return <Pill tone={PERSON_STATUS_TONE[status.state] ?? "neutral"}>{status.label}</Pill>;
}

/**
 * Card 2404 — one watchlist match, in words: "PEP match — PEP class 1: Boris Johnson". The list
 * label ("PEP", "Sanctions", "SIP", "Adverse media") and the summary are the engine's; a hit with
 * no summary still says what kind of match it is.
 */
export function hitLine(hit: JobStatusScreeningHit): string {
  const summary = typeof hit.summary === "string" ? hit.summary.trim() : "";
  return summary === "" ? `${hit.label} match` : `${hit.label} match — ${summary}`;
}

/**
 * A screening: the engine's status, and under it each watchlist match it found — the walk showed
 * "No matches" for the one person on the file who had a PEP hit (card 2404).
 */
export function ScreeningStatus({ status }: { status: JobStatusPersonStatus }): JSX.Element {
  const hits = status.hits ?? [];
  if (hits.length === 0) return <PersonStatus status={status} />;
  return (
    <span className="fd-screen">
      <PersonStatus status={status} />
      <ul className="fd-screen__hits">
        {hits.map((hit, index) => (
          <li key={`${hit.list}-${index}`} className="fd-screen__hit">
            {hitLine(hit)}
          </li>
        ))}
      </ul>
    </span>
  );
}
